import { useState, useEffect, useRef } from 'react';
import { interpolate, uploadEmailAttachment } from '../utils/email';

const attachmentName = (att) => {
  if (typeof att !== 'string') return att.name;
  return att.startsWith('data:') ? null : decodeURIComponent(att.split('/').pop());
};

const EmailPreviewModal = ({ pending, onConfirm, onClose }) => {
  const [sending, setSending]     = useState(false);
  const [done, setDone]           = useState(false);
  const [sendError, setSendError] = useState('');
  const [editSubject, setEditSubject] = useState('');
  const [editBody,    setEditBody]    = useState('');
  const [extraAttachments, setExtraAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileRef = useRef(null);

  const vars = pending
    ? { name: pending.name || '', firstName: pending.firstName || '', event: pending.event || '', date: pending.date || '', dateEnd: pending.dateEnd || '', location: pending.location || '' }
    : {};

  // При открытии инициализируем редактируемые поля интерполированным текстом
  useEffect(() => {
    if (!pending) return;
    setEditSubject(interpolate(pending.subject || '', vars));
    setEditBody(interpolate(pending.body || '', vars));
    setSendError('');
    setDone(false);
    setExtraAttachments([]);
    setUploadError('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending]);

  if (!pending) return null;

  const hasTemplate = !!(pending.subject && pending.body) && pending.emailEnabled !== false;

  const handleFileAdd = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    setUploadError('');
    try {
      const uploaded = await uploadEmailAttachment(file);
      setExtraAttachments(prev => [...prev, uploaded]);
    } catch (err) {
      setUploadError(`Не удалось загрузить файл: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const removeExtraAttachment = (i) => setExtraAttachments(prev => prev.filter((_, j) => j !== i));

  const handleConfirm = async () => {
    setSending(true);
    setSendError('');
    // Передаём уже отредактированный текст напрямую (без повторной интерполяции)
    const attachments = [...(pending.attachments || []), ...extraAttachments];
    const result = await onConfirm({ ...pending, subject: editSubject, body: editBody, attachments, _alreadyInterpolated: true });
    setSending(false);
    if (result && !result.ok) {
      setSendError(`Ошибка отправки: ${result.error}`);
    } else {
      setDone(true);
      setTimeout(() => { setDone(false); onClose(); }, 2200);
    }
  };

  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
    zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
  };
  const box = {
    background: '#fff', borderRadius: 12, boxShadow: '0 8px 40px rgba(0,0,0,0.22)',
    width: '100%', maxWidth: 600, maxHeight: '90vh', display: 'flex', flexDirection: 'column',
  };

  if (done) return (
    <div style={overlay}>
      <div style={{ ...box, alignItems: 'center', justifyContent: 'center', padding: 40, gap: 12 }}>
        <div style={{ fontSize: 48 }}>✅</div>
        <div style={{ fontWeight: 700, fontSize: 18 }}>Письмо отправлено</div>
        <div style={{ color: '#888', fontSize: 14 }}>Уведомление отправлено на {pending.to}</div>
      </div>
    </div>
  );

  return (
    <div style={overlay} onClick={onClose}>
      <div style={box} onClick={e => e.stopPropagation()}>

        {/* Шапка */}
        <div style={{ padding: '18px 22px 14px', borderBottom: '1px solid #eee', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 2 }}>📧 Предпросмотр письма</div>
              <div style={{ fontSize: 13, color: '#888' }}>Кому: <b style={{ color: '#333' }}>{pending.to}</b></div>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#aaa', lineHeight: 1, padding: 0 }}>×</button>
          </div>
        </div>

        {/* Контент */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 22px' }}>
          {!hasTemplate ? (
            <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 8, padding: 16, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <span style={{ fontSize: 22 }}>⚠️</span>
              <div>
                {pending.emailEnabled === false ? (
                  <>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>Письма-подтверждения отключены</div>
                    <div style={{ fontSize: 13, color: '#666' }}>
                      Включить можно в <b>Админке → 📧 Email</b>.
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>Шаблон письма не настроен</div>
                    <div style={{ fontSize: 13, color: '#666' }}>
                      Зайдите в <b>Админку → 📧 Email</b>, заполните шаблон «Подтверждение записи от менеджера» и нажмите «Сохранить».
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#aaa', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                  Тема
                </div>
                <input
                  value={editSubject}
                  onChange={e => setEditSubject(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, fontWeight: 600, boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#aaa', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                  Текст письма — можно редактировать перед отправкой
                </div>
                <textarea
                  value={editBody}
                  onChange={e => setEditBody(e.target.value)}
                  rows={12}
                  style={{
                    width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 6,
                    fontSize: 13, fontFamily: 'inherit', resize: 'vertical', lineHeight: 1.6,
                    boxSizing: 'border-box',
                  }}
                />
              </div>
              {(pending.attachments || []).length > 0 && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#aaa', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Вложения по умолчанию (из шаблона)</div>
                  {pending.attachments.map((att, i) => (
                    <div key={i} style={{ fontSize: 12, color: '#555', display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0' }}>
                      📎 <span style={{ fontWeight: 500 }}>{attachmentName(att) || '(файл без имени)'}</span>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#aaa', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                  Прикрепить файл к этому письму
                </div>
                {extraAttachments.map((att, i) => (
                  <div key={i} style={{ fontSize: 12, color: '#555', display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0' }}>
                    📎 <span style={{ flex: 1, fontWeight: 500 }}>{attachmentName(att) || '(файл без имени)'}</span>
                    <button type="button" onClick={() => removeExtraAttachment(i)}
                      style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontSize: 15, padding: 0 }}>✕</button>
                  </div>
                ))}
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                  style={{ marginTop: 6, padding: '7px 14px', background: '#f0f0f0', border: '1px solid #ddd', borderRadius: 6, cursor: uploading ? 'default' : 'pointer', fontSize: 13, color: '#555' }}>
                  {uploading ? 'Загружаем...' : '📎 Прикрепить файл'}
                </button>
                <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,.gif" style={{ display: 'none' }} onChange={handleFileAdd} />
                {uploadError && <div style={{ marginTop: 6, fontSize: 12, color: '#c62828' }}>{uploadError}</div>}
              </div>
            </>
          )}
        </div>

        {/* Кнопки */}
        <div style={{ padding: '14px 22px', borderTop: '1px solid #eee', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {sendError && (
            <div style={{ background: '#ffebee', border: '1px solid #ffcdd2', borderRadius: 6, padding: '8px 12px', fontSize: 13, color: '#c62828' }}>
              ❌ {sendError}
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button onClick={onClose}
              style={{ padding: '9px 20px', background: '#f0f0f0', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 14, color: '#555' }}>
              {hasTemplate ? 'Не отправлять' : 'Закрыть'}
            </button>
            {hasTemplate && (
              <button onClick={handleConfirm} disabled={sending || uploading || !editSubject.trim() || !editBody.trim()}
                style={{ padding: '9px 24px', background: sending ? '#aaa' : '#db782e', color: '#fff', border: 'none', borderRadius: 7, cursor: sending ? 'default' : 'pointer', fontSize: 14, fontWeight: 600 }}>
                {sending ? 'Отправляем...' : '✉️ Отправить письмо'}
              </button>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default EmailPreviewModal;
